import { jsx as _jsx } from "react/jsx-runtime";
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '../../features/home/context/ThemeContext';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import HomeApp from './index';
let mapRoot = null;
let mapContainer = null;
let isVisible = false;
/**
 * Update the visibility of the map container based on whether we're on the home route
 */
const updateVisibility = (visible) => {
    if (!mapContainer)
        return;
    const wasHidden = !isVisible;
    isVisible = visible;
    if (visible) {
        // Use position: fixed to cover the entire viewport without being affected by body margins
        mapContainer.style.visibility = 'visible';
        mapContainer.style.position = 'fixed';
        mapContainer.style.top = '0';
        mapContainer.style.left = '0';
        mapContainer.style.right = '0';
        mapContainer.style.bottom = '0';
        mapContainer.style.width = '100%';
        mapContainer.style.height = '100%';
        mapContainer.style.overflow = 'visible';
        mapContainer.style.zIndex = '1'; // Above other content but below modals
        mapContainer.removeAttribute('aria-hidden');
        // Prevent body scroll when map is visible
        document.body.style.overflow = 'hidden';
        // When map becomes visible, tell Leaflet to recalculate dimensions
        if (wasHidden) {
            setTimeout(async () => {
                const windowAny = window;
                const map = windowAny.map;
                const LayerAdapter = windowAny.LayerAdapter;
                if (map) {
                    map.invalidateSize();
                    // Re-add the StructureLayer to fix tile grid calculation
                    // When the layer was first added, the container had 0 height
                    if (LayerAdapter) {
                        const structureLayer = await LayerAdapter.getLayer('StructureLayer');
                        if (structureLayer && map.hasLayer(structureLayer)) {
                            map.removeLayer(structureLayer);
                            map.addLayer(structureLayer);
                        }
                    }
                }
                // Dispatch custom event for React components that need to know visibility changed
                window.dispatchEvent(new CustomEvent('mapVisibilityChanged', { detail: { visible: true } }));
            }, 100);
        }
    }
    else {
        mapContainer.style.visibility = 'hidden';
        mapContainer.style.position = 'absolute';
        mapContainer.style.width = '100%';
        mapContainer.style.height = '0';
        mapContainer.style.overflow = 'hidden';
        mapContainer.style.top = '-9999px';
        mapContainer.style.left = '-9999px';
        mapContainer.style.zIndex = '-1';
        mapContainer.setAttribute('aria-hidden', 'true');
        // Restore body scroll when map is hidden
        document.body.style.overflow = '';
    }
};
/**
 * Check if the current path is the home route
 */
const isHomeRoute = () => window.location.pathname === '/';
/**
 * Initialize the separate map root.
 * This should be called once from app-shell.tsx when the user is authenticated.
 */
export const initializeMapRoot = () => {
    // Already initialized
    if (mapRoot) {
        console.log('[MapRoot] Already initialized, updating visibility for path:', window.location.pathname);
        updateVisibility(isHomeRoute());
        return;
    }
    console.log('[MapRoot] Initializing separate React root for map...');
    console.log('[MapRoot] Current path:', window.location.pathname, 'isHomeRoute:', isHomeRoute());
    // Create container element
    mapContainer = document.createElement('div');
    mapContainer.id = 'map-root-container';
    // Insert at the start of body (before app-root)
    const appRoot = document.getElementById('app-root');
    if (appRoot) {
        document.body.insertBefore(mapContainer, appRoot);
    }
    else {
        document.body.appendChild(mapContainer);
    }
    // Set initial visibility
    updateVisibility(isHomeRoute());
    // Create separate React root
    mapRoot = createRoot(mapContainer);
    // Render HomeApp in isolation (with its own providers, no external Router needed)
    mapRoot.render(_jsx(ErrorBoundary, { children: _jsx(ThemeProvider, { defaultTheme: "dark", children: _jsx(HomeApp, {}) }) }));
    console.log('[MapRoot] Map root created and HomeApp rendered');
};
/**
 * Handle navigation events to show/hide the map.
 * This should be called whenever the route changes.
 */
export const handleMapRouteChange = (pathname) => {
    const visible = pathname === '/';
    console.log(`[MapRoot] Route changed to ${pathname}, visibility: ${visible}`);
    updateVisibility(visible);
};
/**
 * Get whether the map is currently visible
 */
export const isMapVisible = () => isVisible;
/**
 * Cleanup the map root (should rarely be needed, but available for edge cases)
 */
export const destroyMapRoot = () => {
    if (mapRoot) {
        console.log('[MapRoot] Destroying map root');
        mapRoot.unmount();
        mapRoot = null;
    }
    if (mapContainer && mapContainer.parentNode) {
        mapContainer.parentNode.removeChild(mapContainer);
        mapContainer = null;
    }
    isVisible = false;
};
